import { cn } from "@/app/utils/cn";
import { percentageMask } from "@/app/utils/comissionPercentage";
import { formatMoneyValue, moneyMask } from "@/app/utils/formatMoneyValue";
import { Trash } from "lucide-react";
import { useState, useEffect } from "react";
import { BasicInput } from "./BasicInput";
import { useFormContext } from "react-hook-form";
import { Button } from "./ui/button";
import { z } from "zod";
import { Spinner } from "./ui/spinner";
import { useDefaultGoals } from "@/app/hooks/useDefaultGoals";


export const GoalsValidation = z
  .object({
    value: z
      .string()
      .min(1, "* obrigatório")
      .transform((v) => Number(v.replace(/\D/g, "")))
      .refine((v) => v >= 0, "* obrigatório"),
    commission: z
      .string()
      .min(1, "* obrigatório")
      .transform((v) => {
        return Number(v.replace(/\D/g, "")) / 100;
      })
      .refine((v) => v >= 0, "* obrigatório"),
    bonus: z
      .string()
      .optional()
      .transform((v) => {
        if (!v) return undefined;
        return Number(v.replace(/\D/g, ""));
      })
      .refine((v) => (v ? v > 0 : true), "* obrigatório"),
  })
  .array()
  .optional()
  .refine(
    (goals) => {
      if (!goals) return true;
      for (let i = 1; i < goals.length; i++) {
        if (
          goals[i].value < goals[i - 1].value ||
          goals[i].commission < goals[i - 1].commission
        ) {
          return false;
        }
      }
      return true;
    },
    {
      message: "*Cada meta deve maior ou igual a meta anterior",
    },
  );

const formSchema = z.object({
  goal: GoalsValidation,
});

type GoalsValues = z.infer<typeof formSchema>;

interface GoalsFormProps {
  useDefault?: boolean;
}
export default function GoalsForm({ useDefault = false }: GoalsFormProps) {
  const methods = useFormContext<GoalsValues>();
  const [goalsAmount, setGoalAmount] = useState(0);

  const { config: configTeamData, isLoading } = useDefaultGoals();

  const defaultGoals = configTeamData?.goal.map((g) => ({
    value: formatMoneyValue(g.value) as unknown as number,
    commission: g.commission.toFixed(2) as unknown as number,
    bonus: formatMoneyValue(g.bonus || 0) as unknown as number,
  }));

  useEffect(() => {
    if (useDefault && !isLoading && !!defaultGoals && defaultGoals.length > 0) {
      methods.setValue("goal", defaultGoals);
      setGoalAmount(defaultGoals.length - 1);
    } else {
      updateGoalAmount();
    }
  }, [useDefault, defaultGoals, methods, isLoading]);

  const handleAddGoal = () => {
    methods.setValue("goal", [
      ...(methods?.getValues("goal") || []),
      {
        value: formatMoneyValue(0) as unknown as number,
        commission: (0).toFixed(2) as unknown as number,
        bonus: formatMoneyValue(0) as unknown as number,
      },
    ]);
    updateGoalAmount();
  };

  const handleRemoveGoal = (i: number) => {
    methods.setValue(
      "goal",
      methods?.getValues("goal")?.filter((_, index) => index !== i + 1),
    );
    updateGoalAmount();
  };

  const updateGoalAmount = () => {
    setGoalAmount((methods.getValues("goal")?.length || 1) - 1);
  };

  const sortGoals = () => {
    const goals = methods.getValues("goal");
    if (!goals) return;
    methods.setValue(
      "goal",
      goals.sort(
        (a, b) =>
          +String(a.value).replace(/\D/g, "") -
          +String(b.value).replace(/\D/g, ""),
      ),
    );
  };

  return (
    <div className="mb-6 flex w-full flex-col items-start rounded-xl border bg-card p-4 text-card-foreground">
      {useDefault && isLoading ? (
        <div className="flex items-center gap-2 self-center py-4">
          <Spinner className="inline" show={isLoading} />
          <p className="text-lg">Carregando...</p>
        </div>
      ) : (
        <>
          <div
            className="grid w-full items-baseline gap-4"
            style={{ gridTemplateColumns: "6fr 3fr 4fr" }}
          >
            <BasicInput
              name={`goal[0].value`}
              style={{ backgroundColor: "#F7F7F7" }}
              error={methods.formState.errors.goal?.[0]?.bonus?.message}
              onInput={moneyMask}
              value={formatMoneyValue(0)}
              disabled
              label="Meta de Movimentação"
            />

            <BasicInput
              name="goal[0].commission"
              style={{ backgroundColor: "#F7F7F7" }}
              error={methods.formState.errors.goal?.[0]?.commission?.message}
              variant="percentage"
              onInput={percentageMask}
              label="Comissão Adicional"
            />

            <BasicInput
              name={`goal[0].bonus`}
              style={{ backgroundColor: "#F7F7F7" }}
              error={methods.formState.errors.goal?.[0]?.bonus?.message}
              onInput={moneyMask}
              value={formatMoneyValue(0)}
              disabled
              label="Bônus"
            />
          </div>

          {new Array(goalsAmount)?.fill(0)?.map((_, i) => (
            <div
              className="group relative grid gap-4"
              key={i}
              style={{ gridTemplateColumns: "6fr 3fr 4fr" }}
            >
              <BasicInput
                name={`goal[${i + 1}].value`}
                style={{ backgroundColor: "#F7F7F7" }}
                error={methods.formState.errors.goal?.[i + 1]?.value?.message}
                onInput={moneyMask}
                onBlur={() => sortGoals()}
              />

              <BasicInput
                name={`goal[${i + 1}].commission`}
                style={{ backgroundColor: "#F7F7F7" }}
                error={
                  methods.formState.errors.goal?.[i + 1]?.commission?.message
                }
                variant="percentage"
                onInput={percentageMask}
              />

              <BasicInput
                name={`goal[${i + 1}].bonus`}
                style={{ backgroundColor: "#F7F7F7" }}
                error={methods.formState.errors.goal?.[i + 1]?.bonus?.message}
                onInput={moneyMask}
              />

              <div className="absolute  right-0 top-2 hidden group-hover:flex">
                <Button
                  type="button"
                  className=""
                  variant="destructive"
                  size="icon"
                  onClick={() => handleRemoveGoal(i)}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          {methods.formState.errors.goal?.message && (
            <p className="text-sm font-light text-error">
              {methods.formState.errors.goal?.message}
            </p>
          )}
          <p
            className={cn("mt-2 cursor-pointer px-2 py-2 text-sm text-primary")}
            onClick={() => handleAddGoal()}
          >
            + Adicionar
          </p>
        </>
      )}
    </div>
  );
}
